import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Lock, Users, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LogoText } from './ui/Logo';
import { COMPANY_LEGAL_NAME } from '@/lib/companyLegalName';

const AVAILABLE_INTEGRATIONS = [
  { key: 'claude', path: '/integrations/claude-desktop' },
  { key: 'chatgpt', path: '/integrations/chatgpt-custom-actions' },
  { key: 'cursor', path: '/integrations/cursor' },
  { key: 'mcp', path: '/integrations/bare-mcp' },
  { key: 'api', path: '/integrations/bare-api' },
  { key: 'n8n', path: '/integrations/n8n' },
  { key: 'zapier', path: '/integrations/zapier' },
  { key: 'dify', path: '/integrations/dify' },
  { key: 'chrome', path: '/integrations/chrome-extension' },
  { key: 'python', path: '/blog/memara-python-sdk-launch' },
  { key: 'x', path: '/integrations/x-bookmarks' },
  { key: 'whatsapp', path: '/integrations/whatsapp-basic' },
] as const;

const Footer: React.FC = () => {
  const { t } = useTranslation('footer');
  const { t: authT } = useTranslation('auth');
  const integrationNames: Record<(typeof AVAILABLE_INTEGRATIONS)[number]['key'], string> = {
    claude: t('availableIntegrations.claude'),
    chatgpt: t('availableIntegrations.chatgpt'),
    cursor: t('availableIntegrations.cursor'),
    mcp: t('availableIntegrations.mcp'),
    api: t('availableIntegrations.api'),
    n8n: t('availableIntegrations.n8n'),
    zapier: t('availableIntegrations.zapier'),
    dify: t('availableIntegrations.dify'),
    chrome: t('availableIntegrations.chrome'),
    python: t('availableIntegrations.python'),
    x: t('availableIntegrations.x'),
    whatsapp: t('availableIntegrations.whatsapp'),
  };
  const integrations = AVAILABLE_INTEGRATIONS.map((item) => ({
    name: integrationNames[item.key],
    path: item.path,
  }));

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Main Footer Content - single column on mobile for simpler layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-6 md:mb-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <LogoText 
              variant="light" 
              size="md" 
              iconSize={32}
              className="text-white mb-4"
            />
            <p className="text-gray-300 text-sm mb-4">
              {t('tagline')}
            </p>
            
            {/* Company Address */}
            <div className="space-y-2 text-sm text-gray-400">
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 mt-0.5 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-300">{t('company')}</p>
                  <p>{t('companyBy', { company: COMPANY_LEGAL_NAME })}</p>
                  <p>{t('address1')}</p>
                  <p>{t('address2')}</p>
                  <p>{t('addressCountry')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-500" />
                <a href="mailto:hello@memara.io" className="hover:text-white transition">
                  hello@memara.io
                </a>
              </div>
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wide">{t('product')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('home')}
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('dashboard')}
                </Link>
              </li>
              <li>
                <Link to="/dashboard?view=integrations" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('integrations')}
                </Link>
              </li>
              <li>
                <Link to="/spaces" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('memorySpaces')}
                </Link>
              </li>
              <li>
                <Link to="/dashboard?view=analytics" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('analytics')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wide">{t('resources')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/docs" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('documentation')}
                </Link>
              </li>
              <li>
                <Link to="/roadmap" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('roadmap')}
                </Link>
              </li>
              <li>
                <Link to="/integrations/chatgpt-custom-actions" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('chatgptSetup')}
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('support')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('blog')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wide">{t('companySection')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/blog/why-our-logo-is-clarks-nutcracker" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('whyLogo')}
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('careers')}
                </Link>
              </li>
              <li>
                <a href="mailto:hello@memara.io" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('contactUs')}
                </a>
              </li>
              <li>
                <Link to="/integrations/claude-desktop" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('claudeDesktop')}
                </Link>
              </li>
              <li>
                <Link to="/comparisons/memara-vs-mem0" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center">
                  {t('vsMem0')}
                </Link>
              </li>
              <li>
                <a href="https://x.com/memaraio" className="text-gray-400 hover:text-white text-sm transition min-h-[44px] min-w-[44px] inline-flex items-center" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                  {t('followUs')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Integrations Section - compact on mobile */}
        <div className="border-t border-gray-800 pt-6 md:pt-8 mb-6 md:mb-8">
          <h3 className="text-sm font-semibold text-gray-300 mb-3 md:mb-4 uppercase tracking-wide">
            {t('integrationsTitle')} ({integrations.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-4">
            {integrations.map((integration) => (
              <Link
                key={integration.path}
                to={integration.path}
                className="text-gray-400 hover:text-white text-sm transition flex items-center p-2 rounded hover:bg-gray-800 min-h-[44px] min-w-[44px]"
              >
                <span>{integration.name}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Trust Indicators - stacked on mobile */}
        <div className="border-t border-gray-800 pt-6 md:pt-8 mb-6 md:mb-8">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <Lock className="w-4 h-4 text-blue-500" aria-hidden />
              <span>{t('trustEncrypted')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-purple-500" aria-hidden />
              <span>{t('trustIsolation')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <KeyRound className="w-4 h-4 text-yellow-500" aria-hidden />
              <span>{t('trustPermissions')}</span>
            </div>
          </div>
        </div>
        
        {/* Bottom Section */}
        <div className="border-t border-gray-800 pt-6 md:pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 mb-4 md:mb-0">
              <p className="text-sm text-gray-400">
                &copy; {new Date().getFullYear()} {t('copyright', { company: COMPANY_LEGAL_NAME })}
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center md:justify-start">
                <Link to="/support" className="text-sm text-gray-400 hover:text-white transition min-h-[44px] inline-flex items-center">
                  {t('helpSupport')}
                </Link>
                <a href="mailto:hello@memara.io" className="text-sm text-gray-400 hover:text-white transition min-h-[44px] inline-flex items-center">
                  {t('contact')}
                </a>
                <Link to="/docs" className="text-sm text-gray-400 hover:text-white transition min-h-[44px] inline-flex items-center">
                  {t('documentation')}
                </Link>
                <Link to="/legal/privacy-policy" className="text-sm text-gray-400 hover:text-white transition min-h-[44px] inline-flex items-center">
                  {authT('privacyPolicy')}
                </Link>
                <Link to="/legal/terms" className="text-sm text-gray-400 hover:text-white transition min-h-[44px] inline-flex items-center">
                  {authT('termsOfService')}
                </Link>
              </div>
            </div>
            
                         <div className="flex space-x-6">
               <a href="https://x.com/memaraio" className="text-gray-400 hover:text-white transition" aria-label="X (Twitter)">
                 <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                   <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                 </svg>
               </a>

              <a href="https://www.linkedin.com/company/memaraio/" className="text-gray-400 hover:text-white transition" aria-label="LinkedIn">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 